import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import Layout from "@/components/Layout";
import { toast } from "sonner";
import {
  ClipboardCheck, ChevronLeft, Filter, Calendar as CalendarIcon,
  CheckCircle2, Clock, XCircle, FileText,
} from "lucide-react";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, Legend,
} from "recharts";

const CAT_LABELS = { tackle: "Tackle", flag_masculino: "Flag Masc.", flag_femenino: "Flag Fem." };
const TYPE_LABELS = { practice: "Entrenamiento", game: "Partido", meeting: "Reunión", other: "Actividad Club" };

const STATUS_META = {
  present:   { label: "Asistió",        color: "#34C759", icon: CheckCircle2 },
  late:      { label: "Llegó tarde",    color: "#FF9F0A", icon: Clock },
  absent:    { label: "Ausente",        color: "#FF3B30", icon: XCircle },
  justified: { label: "Justificado",    color: "#A78BFA", icon: FileText },
};

const RSVP_LABEL = { going: "Voy", late: "Voy tarde", not_going: "No voy" };

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("es-CL", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return iso; }
}

export default function Attendance() {
  const { user } = useAuth();
  const [tab, setTab] = useState("events"); // events | stats
  const [selectedEvent, setSelectedEvent] = useState(null);

  return (
    <Layout>
      <div className="px-6 md:px-10 py-8 max-w-[1500px]">
        <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="overline">Administración</div>
            <h1 className="font-display text-4xl sm:text-5xl text-white leading-none flex items-center gap-3">
              <ClipboardCheck size={36} className="text-cyan-400"/>
              CONTROL DE ASISTENCIA
            </h1>
            <p className="text-sm text-[#A1A1AA] mt-2">Validación oficial de la asistencia real a entrenamientos, partidos, reuniones y actividades.</p>
          </div>
          {!selectedEvent && (
            <div className="flex border border-[#27272A]" data-testid="att-tabs">
              {[
                { k: "events", label: "Eventos" },
                { k: "stats", label: "Estadísticas" },
              ].map(t => (
                <button
                  key={t.k}
                  onClick={() => setTab(t.k)}
                  data-testid={`att-tab-${t.k}`}
                  className={`px-5 py-2 text-xs uppercase tracking-wider font-bold transition-colors ${tab === t.k ? "bg-[#007AFF] text-white" : "text-[#A1A1AA] hover:text-white"}`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {selectedEvent ? (
          <EventValidation
            eventId={selectedEvent.id}
            initialEvent={selectedEvent}
            onBack={() => setSelectedEvent(null)}
            user={user}
          />
        ) : tab === "events" ? (
          <EventList onSelect={setSelectedEvent} user={user}/>
        ) : (
          <StatsPanel user={user}/>
        )}
      </div>
    </Layout>
  );
}

// ---------------- Event List ----------------
function EventList({ onSelect, user }) {
  const now = new Date();
  const [filters, setFilters] = useState({
    year: now.getFullYear(),
    month: "", // "" = todos los meses del año
    category: "",
    event_type: "",
  });
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const isAdmin = ["super_admin", "admin", "admin_directive", "admin_treasurer"].includes(user?.role);
  const myCats = user?.coach_categories || [];

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (filters.year) params.year = filters.year;
    if (filters.month) params.month = filters.month;
    if (filters.category) params.category = filters.category;
    if (filters.event_type) params.event_type = filters.event_type;
    api.get("/attendance/events", { params })
      .then(r => setEvents(r.data || []))
      .catch(err => toast.error(err?.response?.data?.detail || "Error al cargar eventos"))
      .finally(() => setLoading(false));
  }, [filters]);

  const visibleCats = isAdmin
    ? Object.keys(CAT_LABELS)
    : Object.keys(CAT_LABELS).filter(c => myCats.includes(c));

  const years = useMemo(() => {
    const y = now.getFullYear();
    return [y - 1, y, y + 1];
  }, [now]);

  return (
    <div className="space-y-4" data-testid="event-list">
      <div className="tactical-card p-4">
        <div className="flex items-center gap-2 mb-3">
          <Filter size={14} className="text-[#A1A1AA]"/>
          <div className="overline">Filtros</div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#71717A]">Año</label>
            <select className="input-tactical input-tactical-compact text-sm" value={filters.year} onChange={e => setFilters({ ...filters, year: Number(e.target.value) })} data-testid="filter-year">
              {years.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#71717A]">Mes</label>
            <select className="input-tactical input-tactical-compact text-sm" value={filters.month} onChange={e => setFilters({ ...filters, month: e.target.value ? Number(e.target.value) : "" })} data-testid="filter-month">
              <option value="">Todos</option>
              {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                <option key={m} value={m}>{new Date(2000, m - 1, 1).toLocaleDateString("es-CL", { month: "long" })}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#71717A]">Categoría</label>
            <select className="input-tactical input-tactical-compact text-sm" value={filters.category} onChange={e => setFilters({ ...filters, category: e.target.value })} data-testid="filter-category">
              <option value="">Todas</option>
              {visibleCats.map(c => <option key={c} value={c}>{CAT_LABELS[c]}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#71717A]">Tipo</label>
            <select className="input-tactical input-tactical-compact text-sm" value={filters.event_type} onChange={e => setFilters({ ...filters, event_type: e.target.value })} data-testid="filter-type">
              <option value="">Todos</option>
              {Object.entries(TYPE_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="tactical-card p-10 text-center text-[#A1A1AA]">Cargando eventos...</div>
      ) : events.length === 0 ? (
        <div className="tactical-card p-10 text-center text-[#A1A1AA]" data-testid="no-events">
          <CalendarIcon size={32} className="mx-auto mb-3 text-[#52525b]"/>
          No hay eventos pasados que coincidan con los filtros.
        </div>
      ) : (
        <div className="tactical-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#27272A]">
                <th className="text-left p-3 overline">Fecha</th>
                <th className="text-left p-3 overline">Evento</th>
                <th className="text-left p-3 overline">Tipo</th>
                <th className="text-left p-3 overline">Categoría</th>
                <th className="text-left p-3 overline">Validados</th>
                <th className="text-right p-3 overline">Asist.</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {events.map(ev => {
                const c = ev.attendance_counts || {};
                const attended = (c.present || 0) + (c.late || 0);
                const total = c.validated_total || 0;
                const pct = total ? Math.round((attended / total) * 100) : null;
                const needsAttention = ev.needs_attention;
                return (
                  <tr
                    key={ev.id}
                    className={`border-b border-[#27272A] transition-colors ${needsAttention ? "bg-[#2a1505] hover:bg-[#3a1d08]" : "hover:bg-[#121212]"}`}
                    data-testid={`event-row-${ev.id}`}
                  >
                    <td className="p-3 font-mono text-xs text-white whitespace-nowrap">
                      {fmtDate(ev.starts_at)}
                      {needsAttention && <span className="ml-2 text-[9px] uppercase tracking-wider text-[#FF9F0A] border border-[#FF9F0A] px-1.5 py-0.5 font-bold animate-pulse" data-testid="attention-flag">Por validar</span>}
                      {!needsAttention && ev.is_future && <span className="ml-2 text-[9px] uppercase tracking-wider text-cyan-400 border border-cyan-400 px-1 py-0.5">Futuro</span>}
                    </td>
                    <td className="p-3 text-white">{ev.title}</td>
                    <td className="p-3 text-xs text-[#A1A1AA]">{TYPE_LABELS[ev.event_type] || ev.event_type}</td>
                    <td className="p-3 text-xs">
                      {(ev.categories || []).length === 0 ? (
                        <span className="badge text-[10px]">General</span>
                      ) : (ev.categories || []).map(c => (
                        <span key={c} className="badge badge-coach text-[10px] mr-1 whitespace-nowrap">{CAT_LABELS[c]}</span>
                      ))}
                    </td>
                    <td className="p-3 font-mono text-xs">{total}</td>
                    <td className="p-3 text-right font-mono text-xs">
                      {pct === null ? <span className="text-[#52525b]">—</span> : <span className="text-cyan-400">{pct}%</span>}
                    </td>
                    <td className="p-3 text-right">
                      <button
                        onClick={() => onSelect(ev)}
                        className={`text-[10px] px-3 py-1.5 ${needsAttention ? "btn-primary !bg-[#FF9F0A] !text-black" : "btn-primary"}`}
                        data-testid={`validate-btn-${ev.id}`}
                      >
                        {ev.is_future ? "VER" : "VALIDAR"}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---------------- Event Validation Detail ----------------
function EventValidation({ eventId, initialEvent, onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // local edits keyed by user_id: { status, reason }
  const [edits, setEdits] = useState({});
  const [selected, setSelected] = useState(new Set());

  const load = () => {
    setLoading(true);
    api.get(`/attendance/events/${eventId}`)
      .then(r => {
        setData(r.data);
        // seed edits with current validated states
        const seed = {};
        (r.data.players || []).forEach(p => {
          seed[p.user_id] = { status: p.attendance || "", reason: p.reason || "" };
        });
        setEdits(seed);
      })
      .catch(err => toast.error(err?.response?.data?.detail || "Error al cargar evento"))
      .finally(() => setLoading(false));
  };
  useEffect(load, [eventId]);

  const players = data?.players || [];
  const counts = useMemo(() => {
    const c = { present: 0, late: 0, absent: 0, justified: 0, unset: 0 };
    players.forEach(p => {
      const s = edits[p.user_id]?.status;
      if (s && c[s] !== undefined) c[s]++;
      else c.unset++;
    });
    return c;
  }, [edits, players]);

  if (loading) return <div className="tactical-card p-10 text-center text-[#A1A1AA]">Cargando...</div>;
  if (!data) return null;
  const ev = data.event || initialEvent || {};

  const updateField = (uid, field, val) => {
    setEdits(prev => ({ ...prev, [uid]: { ...(prev[uid] || {}), [field]: val } }));
  };

  const toggleSelect = (uid) => {
    setSelected(prev => {
      const n = new Set(prev);
      if (n.has(uid)) n.delete(uid);
      else n.add(uid);
      return n;
    });
  };
  const toggleSelectAll = () => {
    if (selected.size === players.length) setSelected(new Set());
    else setSelected(new Set(players.map(p => p.user_id)));
  };

  const applyToSelected = (status) => {
    if (selected.size === 0) {
      toast.message("Selecciona jugadores primero");
      return;
    }
    setEdits(prev => {
      const next = { ...prev };
      selected.forEach(uid => {
        next[uid] = { ...(next[uid] || {}), status };
      });
      return next;
    });
  };

  const markAllPresent = () => {
    setEdits(prev => {
      const next = { ...prev };
      players.forEach(p => { next[p.user_id] = { ...(next[p.user_id] || {}), status: "present" }; });
      return next;
    });
  };

  const copyFromRsvp = () => {
    const map = { going: "present", late: "late", not_going: "absent" };
    setEdits(prev => {
      const next = { ...prev };
      players.forEach(p => {
        const m = map[p.rsvp];
        if (m) next[p.user_id] = { ...(next[p.user_id] || {}), status: m };
      });
      return next;
    });
    toast.success("Estados copiados desde RSVP");
  };

  const save = async () => {
    const entries = [];
    players.forEach(p => {
      const e = edits[p.user_id] || {};
      if (!e.status) return; // skip empty (sin validar)
      // Only push if changed or new
      const changed = (e.status !== p.attendance) || ((e.reason || "") !== (p.reason || ""));
      if (changed) {
        entries.push({ user_id: p.user_id, status: e.status, reason: e.reason || null });
      }
    });
    if (entries.length === 0) {
      toast.message("No hay cambios para guardar");
      return;
    }
    setSaving(true);
    try {
      await api.put(`/attendance/events/${eventId}`, { entries });
      toast.success(`${entries.length} jugador(es) actualizado(s)`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="event-validation">
      <button onClick={onBack} className="text-cyan-400 hover:text-white text-xs uppercase tracking-wider flex items-center gap-1" data-testid="back-to-list">
        <ChevronLeft size={14}/> Volver a la lista
      </button>

      <div className="tactical-card p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="overline">{TYPE_LABELS[ev.event_type] || ev.event_type}</div>
            <h2 className="font-display text-3xl text-white mt-1">{ev.title}</h2>
            <div className="text-sm text-[#A1A1AA] mt-1 flex items-center gap-2 flex-wrap">
              <span className="font-mono">{fmtDate(ev.starts_at)}</span>
              {(ev.categories || []).map(c => (
                <span key={c} className="badge badge-coach text-[10px] whitespace-nowrap">{CAT_LABELS[c]}</span>
              ))}
              {(ev.categories || []).length === 0 && <span className="badge text-[10px]">General</span>}
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            {Object.entries(STATUS_META).map(([k, m]) => {
              const Icon = m.icon;
              return (
                <div key={k} className="text-center min-w-[70px]" data-testid={`count-${k}`}>
                  <div className="flex items-center gap-1 justify-center text-[10px] uppercase tracking-wider" style={{ color: m.color }}>
                    <Icon size={10}/> {m.label}
                  </div>
                  <div className="font-display text-2xl text-white">{counts[k]}</div>
                </div>
              );
            })}
            <div className="text-center min-w-[70px]" data-testid="count-unset">
              <div className="text-[10px] uppercase tracking-wider text-[#71717A]">Sin validar</div>
              <div className="font-display text-2xl text-[#71717A]">{counts.unset}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick actions */}
      <div className="tactical-card p-4 flex flex-wrap items-center gap-2">
        <div className="overline text-cyan-400 mr-2">Acciones rápidas:</div>
        <button onClick={markAllPresent} className="btn-ghost text-[10px]" data-testid="mark-all-present">TODOS ASISTIERON</button>
        <button onClick={copyFromRsvp} className="btn-ghost text-[10px]" data-testid="copy-from-rsvp">COPIAR DESDE RSVP</button>
        <div className="border-l border-[#27272A] h-6 mx-2"/>
        <div className="overline">Marcar seleccionados como:</div>
        {Object.entries(STATUS_META).map(([k, m]) => (
          <button
            key={k}
            onClick={() => applyToSelected(k)}
            disabled={selected.size === 0}
            className="text-[10px] uppercase tracking-wider font-bold border px-3 py-1.5 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ borderColor: m.color, color: m.color }}
            data-testid={`apply-selected-${k}`}
          >
            {m.label} ({selected.size})
          </button>
        ))}
      </div>

      {/* Players table */}
      <div className="tactical-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#27272A]">
              <th className="p-3 w-10">
                <input type="checkbox" checked={selected.size === players.length && players.length > 0} onChange={toggleSelectAll} data-testid="select-all"/>
              </th>
              <th className="text-left p-3 overline">Jugador</th>
              <th className="text-left p-3 overline">Categoría</th>
              <th className="text-left p-3 overline">RSVP</th>
              <th className="text-left p-3 overline">Estado final</th>
              <th className="text-left p-3 overline">Motivo (opcional)</th>
            </tr>
          </thead>
          <tbody>
            {players.length === 0 && (
              <tr><td colSpan="6" className="p-6 text-center text-[#A1A1AA]">No hay jugadores elegibles para este evento.</td></tr>
            )}
            {players.map(p => {
              const e = edits[p.user_id] || {};
              const meta = STATUS_META[e.status];
              return (
                <tr key={p.user_id} className="border-b border-[#27272A]" data-testid={`player-row-${p.user_id}`}>
                  <td className="p-3">
                    <input type="checkbox" checked={selected.has(p.user_id)} onChange={() => toggleSelect(p.user_id)} data-testid={`select-${p.user_id}`}/>
                  </td>
                  <td className="p-3 text-white whitespace-nowrap">{p.name}</td>
                  <td className="p-3 text-xs">
                    {(p.categories || []).map(c => (
                      <span key={c} className="badge badge-coach text-[10px] mr-1 whitespace-nowrap">{CAT_LABELS[c]}</span>
                    ))}
                  </td>
                  <td className="p-3 text-xs text-[#A1A1AA] whitespace-nowrap">{RSVP_LABEL[p.rsvp] || "—"}</td>
                  <td className="p-3">
                    <select
                      value={e.status || ""}
                      onChange={(ev2) => updateField(p.user_id, "status", ev2.target.value)}
                      className="input-tactical input-tactical-compact text-sm w-full"
                      style={meta ? { borderColor: meta.color, color: meta.color } : undefined}
                      data-testid={`status-select-${p.user_id}`}
                    >
                      <option value="">— Sin validar —</option>
                      {Object.entries(STATUS_META).map(([k, m]) => (
                        <option key={k} value={k}>{m.label}</option>
                      ))}
                    </select>
                  </td>
                  <td className="p-3">
                    <input
                      type="text"
                      value={e.reason || ""}
                      onChange={(ev2) => updateField(p.user_id, "reason", ev2.target.value)}
                      placeholder={e.status === "justified" ? "Motivo (ej: certificado médico)" : ""}
                      className="input-tactical input-tactical-compact text-sm w-full"
                      data-testid={`reason-${p.user_id}`}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="flex justify-end gap-2 sticky bottom-2">
        <button onClick={onBack} className="btn-ghost text-xs" data-testid="cancel-validation">CANCELAR</button>
        <button onClick={save} disabled={saving} className="btn-primary text-xs" data-testid="save-validation">
          {saving ? "Guardando..." : "GUARDAR ASISTENCIA"}
        </button>
      </div>
    </div>
  );
}

// ---------------- Stats Panel ----------------
function StatsPanel({ user }) {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [category, setCategory] = useState("");
  const [eventType, setEventType] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const isAdmin = ["super_admin", "admin", "admin_directive", "admin_treasurer"].includes(user?.role);
  const myCats = user?.coach_categories || [];
  const visibleCats = isAdmin ? Object.keys(CAT_LABELS) : Object.keys(CAT_LABELS).filter(c => myCats.includes(c));

  useEffect(() => {
    setLoading(true);
    const params = { year };
    if (category) params.category = category;
    if (eventType) params.event_type = eventType;
    api.get("/attendance/stats", { params })
      .then(r => setData(r.data))
      .catch(err => toast.error(err?.response?.data?.detail || "Error al cargar estadísticas"))
      .finally(() => setLoading(false));
  }, [year, category, eventType]);

  if (loading) return <div className="tactical-card p-10 text-center text-[#A1A1AA]">Cargando...</div>;
  if (!data) return null;

  const catData = Object.entries(data.by_category || {})
    .filter(([c]) => visibleCats.includes(c))
    .map(([c, v]) => ({ name: CAT_LABELS[c] || c, rate: v.attendance_rate, total: v.total }));

  const typeData = Object.entries(data.by_event_type || {})
    .map(([t, v]) => ({ name: TYPE_LABELS[t] || t, rate: v.attendance_rate, total: v.total }))
    .filter(d => d.total > 0);

  const monthly = (data.monthly || []).map(m => ({
    name: m.month,
    rate: m.attendance_rate,
    present: m.present + m.late,
    total: m.total,
  }));

  const totalValidated = Object.values(data.by_category || {}).reduce((acc, v) => acc + v.total, 0);

  return (
    <div className="space-y-4" data-testid="stats-panel">
      <div className="tactical-card p-4">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#71717A]">Año</label>
            <select className="input-tactical input-tactical-compact text-sm" value={year} onChange={e => setYear(Number(e.target.value))} data-testid="stats-year">
              {[now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1].map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#71717A]">Categoría</label>
            <select className="input-tactical input-tactical-compact text-sm" value={category} onChange={e => setCategory(e.target.value)} data-testid="stats-category">
              <option value="">Todas</option>
              {visibleCats.map(c => <option key={c} value={c}>{CAT_LABELS[c]}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#71717A]">Tipo</label>
            <select className="input-tactical input-tactical-compact text-sm" value={eventType} onChange={e => setEventType(e.target.value)} data-testid="stats-type">
              <option value="">Todos</option>
              {Object.entries(TYPE_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </div>
        </div>
      </div>

      {totalValidated === 0 ? (
        <div className="tactical-card p-10 text-center text-[#A1A1AA]" data-testid="stats-empty">
          Aún no hay asistencias validadas para los filtros seleccionados.
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="tactical-card p-4">
              <div className="overline mb-3">% Asistencia por categoría</div>
              <div style={{ width: "100%", height: 240 }}>
                <ResponsiveContainer>
                  <BarChart data={catData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#27272A"/>
                    <XAxis dataKey="name" stroke="#A1A1AA" fontSize={11}/>
                    <YAxis stroke="#A1A1AA" fontSize={11} domain={[0, 100]}/>
                    <Tooltip contentStyle={{ background: "#0A0A0A", border: "1px solid #27272A" }} formatter={(v) => [`${v}%`, "Asistencia"]}/>
                    <Bar dataKey="rate" fill="#22d3ee" radius={[4, 4, 0, 0]}/>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="tactical-card p-4">
              <div className="overline mb-3">% Asistencia por tipo de evento</div>
              <div style={{ width: "100%", height: 240 }}>
                <ResponsiveContainer>
                  <BarChart data={typeData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#27272A"/>
                    <XAxis dataKey="name" stroke="#A1A1AA" fontSize={11}/>
                    <YAxis stroke="#A1A1AA" fontSize={11} domain={[0, 100]}/>
                    <Tooltip contentStyle={{ background: "#0A0A0A", border: "1px solid #27272A" }} formatter={(v) => [`${v}%`, "Asistencia"]}/>
                    <Bar dataKey="rate" fill="#34C759" radius={[4, 4, 0, 0]}/>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          <div className="tactical-card p-4">
            <div className="overline mb-3">Tendencia mensual</div>
            <div style={{ width: "100%", height: 260 }}>
              <ResponsiveContainer>
                <LineChart data={monthly}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272A"/>
                  <XAxis dataKey="name" stroke="#A1A1AA" fontSize={11}/>
                  <YAxis stroke="#A1A1AA" fontSize={11} domain={[0, 100]}/>
                  <Tooltip contentStyle={{ background: "#0A0A0A", border: "1px solid #27272A" }} formatter={(v) => [`${v}%`, "Asistencia"]}/>
                  <Legend wrapperStyle={{ fontSize: 11, color: "#A1A1AA" }}/>
                  <Line type="monotone" dataKey="rate" name="% Asistencia" stroke="#22d3ee" strokeWidth={2} dot={{ r: 4 }}/>
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="tactical-card overflow-x-auto" data-testid="top-events">
            <div className="p-4 border-b border-[#27272A] overline">Eventos recientes — asistentes</div>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#27272A]">
                  <th className="text-left p-3 overline">Fecha</th>
                  <th className="text-left p-3 overline">Evento</th>
                  <th className="text-left p-3 overline">Tipo</th>
                  <th className="text-right p-3 overline">Asistieron</th>
                  <th className="text-right p-3 overline">Validados</th>
                  <th className="text-right p-3 overline">%</th>
                </tr>
              </thead>
              <tbody>
                {(data.top_events || []).map(ev => (
                  <tr key={ev.id} className="border-b border-[#27272A]">
                    <td className="p-3 font-mono text-xs text-white whitespace-nowrap">{fmtDate(ev.starts_at)}</td>
                    <td className="p-3 text-white">{ev.title}</td>
                    <td className="p-3 text-xs text-[#A1A1AA]">{TYPE_LABELS[ev.event_type] || ev.event_type}</td>
                    <td className="p-3 font-mono text-xs text-right text-[#34C759]">{ev.present + ev.late}</td>
                    <td className="p-3 font-mono text-xs text-right">{ev.total}</td>
                    <td className="p-3 font-mono text-xs text-right text-cyan-400">{ev.attendance_rate}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
